import React, { useEffect, useState } from "react"
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material"
import { GallerySection } from "@/shared/types/product"
import { EditableGallerySectionField } from "@/features/dashboard/hooks/useGallery"
import { PRODUCT_CATEGORIES } from "@/shared/constants/categories"

interface Props {
  section: GallerySection
  onFieldChange: (sectionId: number, field: EditableGallerySectionField, value: string) => void
  onKeywordsChange: (sectionId: number, keywords: string[]) => void
  generatingText: boolean
  textGenerationBusy: boolean
  canGenerateText: boolean
  onGenerateText: () => void
}

const parseKeywords = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(",")
        .map(keyword => keyword.trim().slice(0, 60))
        .filter(Boolean)
        .slice(0, 10)
    )
  )

const ProductForm: React.FC<Props> = ({
  section,
  onFieldChange,
  onKeywordsChange,
  generatingText,
  textGenerationBusy,
  canGenerateText,
  onGenerateText
}) => {
  const [keywordsInput, setKeywordsInput] = useState(section.keywords.join(", "))

  useEffect(() => {
    const parsedInput = parseKeywords(keywordsInput)
    const matchesSectionKeywords =
      parsedInput.length === section.keywords.length &&
      parsedInput.every((keyword, index) => keyword === section.keywords[index])

    if (!matchesSectionKeywords) {
      setKeywordsInput(section.keywords.join(", "))
    }
  }, [section.keywords, keywordsInput])

  return (
    <Grid container spacing={2} sx={{ mt: 1, pb: 2 }}>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1}>
          <Typography variant="subtitle2" color="text.secondary">
            Product details
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Product Name"
          value={section.name}
          onChange={e => onFieldChange(section.id, "name", e.target.value)}
          inputProps={{ maxLength: 200 }}
          helperText={`${section.name.length}/200`}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={section.description}
          onChange={e => onFieldChange(section.id, "description", e.target.value)}
          multiline
          inputProps={{ maxLength: 500 }}
          helperText={`${section.description.length}/500`}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Keywords"
          value={keywordsInput}
          onChange={e => {
            setKeywordsInput(e.target.value)
            onKeywordsChange(section.id, parseKeywords(e.target.value))
          }}
          inputProps={{ maxLength: 620 }}
          helperText="Comma-separated SEO tags (up to 10 keywords)."
        />
        {section.keywords.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              {section.keywords.map(keyword => (
                <Chip
                  key={keyword}
                  label={keyword}
                  size="small"
                  onDelete={() => {
                    const nextKeywords = section.keywords.filter(item => item !== keyword)
                    setKeywordsInput(nextKeywords.join(", "))
                    onKeywordsChange(section.id, nextKeywords)
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl size="small" fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            size="small"
            label="Category"
            value={section.category}
            onChange={e => onFieldChange(section.id, "category", e.target.value)}>
            {PRODUCT_CATEGORIES.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          size="small"
          fullWidth
          label="Price"
          type="number"
          value={section.price}
          onChange={e => onFieldChange(section.id, "price", e.target.value)}
          inputProps={{ min: 0, inputMode: "decimal" }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₹</InputAdornment>
          }}
        />
      </Grid>
    </Grid>
  )
}

export default ProductForm
